import {
  Box,
  Table as T,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '../../models/CommonModels';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import TableEmptyState from './TableEmptyState';
import { Header, Row, TableProps } from './TableModels';

function Table({
  headers,
  rows,
  onSort,
  defaultOrderBy,
  defaultOrder = 'asc',
  isLoading,
  emptyStateMessage,
}: TableProps) {
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [t] = useTranslation();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return () => {};

    /**
     * Function to check if the table has scroll and/or is at the end of the scroll, meaning we need to disable the action column box-shadow
     */
    const update = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 1;
      const atRightEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

      // show shadow when there is overflow not fully scrolled to the right
      const show = hasOverflow && !atRightEnd ? '1' : '0';
      el.style.setProperty('--sticky-actions-shadow', show);
    };

    update();

    el.addEventListener('scroll', update, { passive: true });

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (containerRef) {
      requestAnimationFrame(() => {
        if (containerRef.current !== null) {
          containerRef.current.scrollLeft = scrollLeft;
        }
      });
    }
  }, [rows]);

  /**
   * On sort click handle
   * @param headerCell
   * @param newOrder
   * @param isLastColumn
   */
  const onSortHandle = (headerCell: Header, newOrder: Order, isLastColumn: boolean) => {
    const valToScroll = containerRef.current?.scrollLeft ?? 0;
    setScrollLeft(isLastColumn ? valToScroll + 100 : valToScroll);
    onSort?.({ orderBy: headerCell.id, order: newOrder });
  };

  /**
   * Get row background color
   * @param row
   * @param index
   */
  const getRowBackgroundColor = (row: Row, index: number) => {
    if (row.hasErrors) {
      return theme.palette.table.error;
    }
    if (!row.hasErrors && index % 2 !== 0) {
      return theme.palette.monochrome.monoChrome2;
    }
    return theme.palette.monochrome.monoChrome1;
  };

  return (
    <TableContainer ref={containerRef} sx={{ overflowX: 'auto' }}>
      <T>
        <TableHead>
          <TableRow>
            {headers.map((headerCell, index) => (
              <TableCell
                key={headerCell.id}
                sortDirection={orderBy === headerCell.id ? order : false}
                sx={{
                  padding: '0.75rem 0.688rem',
                  borderBottom: `1px solid ${theme.palette.monochrome.monoChrome3}`,
                  backgroundColor: theme.palette.monochrome.monoChrome2,
                  ...headerCell.sx,
                }}
              >
                {headerCell.isSortable ? (
                  <TableSortLabel
                    IconComponent={ChevronDownIcon}
                    active={orderBy === headerCell.id}
                    direction={orderBy === headerCell.id ? order : 'asc'}
                    onClick={() => {
                      if (onSort) {
                        const newOrder = order === 'asc' ? 'desc' : 'asc';
                        setOrder(newOrder);
                        setOrderBy(headerCell.id);
                        onSortHandle(headerCell, newOrder, index === headers.length - 1);
                      }
                    }}
                    sx={{
                      fontSize: '1.125rem',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '1.75rem',
                      '& svg': {
                        marginLeft: '0.5rem',
                      },
                    }}
                  >
                    {headerCell.label}
                  </TableSortLabel>
                ) : (
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '1.75rem',
                      '& svg': {
                        marginLeft: '0.5rem',
                      },
                    }}
                  >
                    {headerCell.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id} {...row} sx={{ backgroundColor: getRowBackgroundColor(row, index) }}>
              {row.cells.map((cell, idx) => {
                const checkForError = cell.hasError ? `${theme.palette.state.error}!important` : 'inherit';
                return (
                  <TableCell
                    key={`${row.id}_${headers[idx].id}`}
                    sx={{
                      a: {
                        color: isLoading ? `${theme.palette.monochrome.monoChrome14}!important` : 'inherit',
                        textDecorationColor: isLoading
                          ? `${theme.palette.monochrome.monoChrome14}!important`
                          : 'inherit',
                      },
                      color: isLoading ? `${theme.palette.monochrome.monoChrome14}!important` : checkForError,
                      fontSize: '1.125rem',
                      fontStyle: 'normal',
                      lineHeight: '1.625rem',
                      minHeight: '3rem',
                      padding: '0.688rem 1rem',
                      borderBottom: `1px solid ${
                        isLoading ? theme.palette.monochrome.monoChrome14 : theme.palette.monochrome.monoChrome3
                      }`,
                      ...(cell.isCheckbox && {
                        borderRight: `1px solid ${
                          isLoading ? theme.palette.monochrome.monoChrome14 : theme.palette.monochrome.monoChrome3
                        }`,
                        padding: '0 0.5rem',
                        textAlign: 'center',
                      }),
                      ...cell.sx,
                    }}
                  >
                    {cell.value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
        {!rows.length && !isLoading ? <TableEmptyState message={emptyStateMessage} /> : null}
      </T>
      {isLoading && !rows.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '3rem', paddingBottom: '3rem' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LoadingSpinner style={{ minHeight: '148px', maxWidth: '148px' }} />
            <Typography sx={{ fontSize: '1.75rem', lineHeight: '2rem', fontFamily: 'Vodafone Lt' }}>
              {t('common.table.loadingResults')}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </TableContainer>
  );
}
export default Table;
